import React, { useState, useMemo, useEffect } from "react";
import moment from "moment";

import ClubbieModal from "../../../Modal/ClubbieModal";
import ClubbieSelect from "../../../ClubbieSelect/ClubbieSelect";
import TimePicker from "../../../TimePicker/TimePicker";
import DatePicker from "../../../DatePicker/DatePicker";
import { ReactComponent as InfoIcon } from "../../../../assets/images/icons/info.svg";
import {
  INITIAL_KNOCKOUT_CONFIG,
  INITIAL_GROUP_CONFIG,
  INITIAL_PHASE2_SETTINGS,
} from "../../../../pages/tournament/format/constants";

const TEMPLATE_LABELS = {
  knockoutPhase: "knockout stage",
  groupPhase: "group stage",
  groupStageKnockout: "format structure",
};

const TEAM_PRESETS = [2, 4, 8, 16, 32, 64, 128];
const TEAMS_COUNT_OPTIONS = [2, 4, 8, 16, 32];

/**
 * Smart group config based on category team count.
 *
 * Strategy: pick the divisor that gives groups of 3, 4, or 5 teams (in that
 * order of preference — smaller groups → more groups → cleaner knockout).
 * Falls back to a single group of all teams when no good divisor exists
 * (e.g. primes like 7, 11, 13).
 *
 * Examples:
 *   4 → 1×4    6 → 2×3    8 → 2×4    9 → 3×3
 *   10 → 2×5  12 → 4×3   15 → 5×3   16 → 4×4
 *   7 → 1×7 (no clean divisor)
 */
const PREFERRED_TEAMS_PER_GROUP = [3, 4, 5];

const computeSmartGroupConfig = (teamCount) => {
  if (!teamCount || teamCount < 2) return null;

  for (const tpg of PREFERRED_TEAMS_PER_GROUP) {
    if (teamCount % tpg === 0) {
      return { numGroups: teamCount / tpg, teamsPerGroup: tpg };
    }
  }

  return { numGroups: 1, teamsPerGroup: teamCount };
};

/**
 * Snap a desired bracket size UP to the smallest allowed TEAMS_COUNT_OPTIONS
 * value that is >= desired (so every team gets a bracket slot, with byes if
 * needed). Caps at the largest allowed value when desired exceeds it.
 */
const snapUpToAllowedTeamsCount = (desired) => {
  if (!desired || desired <= TEAMS_COUNT_OPTIONS[0]) return TEAMS_COUNT_OPTIONS[0];
  for (const v of TEAMS_COUNT_OPTIONS) {
    if (v >= desired) return v;
  }
  return TEAMS_COUNT_OPTIONS[TEAMS_COUNT_OPTIONS.length - 1];
};

/**
 * Smart knockout bracket size.
 * - Pure knockout: smallest allowed value >= teamCount, so every team has a
 *   slot (top seeds get byes when teamCount isn't a power of 2).
 *   e.g. 7 teams → 8-team bracket; 9 teams → 16-team bracket.
 * - Group + knockout: floor(teamCount/2) advance, snapped UP to allowed
 *   values. Using floor (not ceil) ensures that for awkward counts like 9
 *   we don't end up with a knockout that's almost as big as the field
 *   (e.g. 9 teams → 4-team KO, eliminating 5 in groups, instead of an
 *   8-team KO eliminating only 1).
 *   e.g. 6→4, 7→4, 8→4, 9→4, 16→8, 32→16. The `numGroups` param is unused
 *   here — half-advance is a function of total team count.
 */
const computeSmartKnockoutTeamsCount = (teamCount, numGroups, isGroupStageKnockout = false) => {
  if (!teamCount || teamCount < 2) return null;
  if (isGroupStageKnockout) {
    return snapUpToAllowedTeamsCount(Math.floor(teamCount / 2));
  }
  return snapUpToAllowedTeamsCount(teamCount);
};

/**
 * Build smart defaults from tournament + previous phase + static fallbacks.
 */
const getPhaseDefaults = (tournament, previousPhase, templateId) => {
  const source = previousPhase || null;
  const ts = tournament?.matchSchedule || {};

  return {
    name:
      {
        knockoutPhase: "Knockout stage",
        groupPhase: "Group stage",
        groupStageKnockout: "Group stage",
      }[templateId] || "",
    availablePitches: source?.availablePitches || getAllPitches(tournament),
    matchRules: {
      gameDuration: source?.matchRules?.gameDuration || "30",
      breaks: source?.matchRules?.breaks || "2",
      breakDuration: source?.matchRules?.breakDuration || "5",
      intervalBetweenMatches: source?.matchRules?.intervalBetweenMatches || "5",
      pointsForWin: source?.matchRules?.pointsForWin || "3",
      pointsForDraw: source?.matchRules?.pointsForDraw || "1",
      pointsForLoss: source?.matchRules?.pointsForLoss || "0",
    },
    matchSchedule: {
      startDate: ts.startDate || "",
      endDate: ts.endDate || "",
      matchDays:
        source?.matchSchedule?.matchDays || ts.matchDays || [],
      matchTimings:
        source?.matchSchedule?.matchTimings || ts.matchTimings || [],
      startTime:
        source?.matchSchedule?.startTime || ts.startTime || "09:00",
      dayEndTime:
        source?.matchSchedule?.dayEndTime || ts.dayEndTime || "21:00",
    },
  };
};

function getAllPitches(tournament) {
  if (tournament?.matchVenue?.pitches?.length) {
    return tournament.matchVenue.pitches.map((p) => p.name || p);
  }
  const count = tournament?.matchVenue?.numberOfPitches || 0;
  if (count > 0) {
    return Array.from({ length: count }, (_, i) => `Pitch ${String.fromCharCode(65 + i)}`);
  }
  return [];
}

/**
 * CreateFormatModal — Single-page form for template-based format creation.
 *
 * Replaces the multi-step PhaseSettingsModal wizard for Knockout, Group,
 * and Group+Knockout templates. Shows only the must-decide fields by default
 * and hides everything else behind a "More settings" disclosure.
 */
const LEG_OPTIONS = [
  { value: 1, label: "Single-leg" },
  { value: 2, label: "Two legs" },
];

const InlineSelect = ({ value, options, onChange }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const ref = React.useRef(null);
  const selected = options.find((o) => o.value === value);

  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className={`h-10 px-4 bg-app-surface border rounded-full flex items-center gap-2 text-[14px] text-[#FEFCEB] font-proxima transition-colors ${
          isOpen ? "border-[#00E7FF]" : "border-app-surface"
        }`}
      >
        <span>{selected?.label}</span>
        <svg
          className={`w-3 h-3 text-app-text-secondary transition-transform ${isOpen ? "rotate-180" : ""}`}
          viewBox="0 0 12 8"
          fill="none"
        >
          <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute top-[calc(100%+4px)] left-0 min-w-full bg-app-surface border border-app-border rounded-xl shadow-xl z-[1000] overflow-hidden">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`w-full px-4 py-2.5 text-left text-[14px] transition-colors ${
                value === opt.value
                  ? "text-[#00E7FF] bg-app-hover"
                  : "text-[#FEFCEB] hover:bg-app-hover"
              }`}
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ALL_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const CreateFormatModal = ({
  isOpen,
  onClose,
  templateType,
  selectedTournament,
  phases = [],
  categoryTeamCount = 0,
  onSubmit,
  isLoading = false,
}) => {
  // ── Defaults ────────────────────────────────────────────
  const previousPhase = phases.length > 0 ? phases[phases.length - 1] : null;
  const defaults = useMemo(
    () => getPhaseDefaults(selectedTournament, previousPhase, templateType),
    [selectedTournament, previousPhase, templateType]
  );

  // ── Smart defaults from category team count ─────────────
  const smartGroupConfig = useMemo(
    () => computeSmartGroupConfig(categoryTeamCount),
    [categoryTeamCount]
  );
  // Knockout-only: bracket fits all teams (next power of 2 >= teamCount).
  // Group+knockout: half of teams advance (snapped up to allowed values).
  // Group-only: this value isn't used.
  const smartBracketTeamsCount = useMemo(
    () => computeSmartKnockoutTeamsCount(
      categoryTeamCount,
      smartGroupConfig?.numGroups,
      templateType === "groupStageKnockout"
    ),
    [categoryTeamCount, smartGroupConfig, templateType]
  );

  // ── Form state ──────────────────────────────────────────
  const [formData, setFormData] = useState(defaults);
  const [groupConfig, setGroupConfig] = useState({
    ...INITIAL_GROUP_CONFIG,
    ...(smartGroupConfig || {}),
  });
  const [bracketConfig, setBracketConfig] = useState({
    ...INITIAL_KNOCKOUT_CONFIG,
    ...(smartBracketTeamsCount ? { teamsCount: smartBracketTeamsCount } : {}),
  });
  const [phase2Settings, setPhase2Settings] = useState({ ...INITIAL_PHASE2_SETTINGS });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customTeams, setCustomTeams] = useState(false);

  // Match days filter (day-of-week) — separate for group and knockout
  const [matchDays, setMatchDays] = useState(ALL_DAYS);
  const [knockoutMatchDays, setKnockoutMatchDays] = useState(ALL_DAYS);

  // Reset state when modal opens or template changes
  useEffect(() => {
    if (isOpen) {
      const d = getPhaseDefaults(selectedTournament, previousPhase, templateType);
      setFormData(d);
      setGroupConfig({
        ...INITIAL_GROUP_CONFIG,
        ...(smartGroupConfig || {}),
      });
      setBracketConfig({
        ...INITIAL_KNOCKOUT_CONFIG,
        ...(smartBracketTeamsCount ? { teamsCount: smartBracketTeamsCount } : {}),
      });
      // For group+knockout, split dates ~80/20
      let koSchedule = { ...d.matchSchedule };
      if (templateType === "groupStageKnockout" && d.matchSchedule.startDate && d.matchSchedule.endDate) {
        const start = moment(d.matchSchedule.startDate);
        const end = moment(d.matchSchedule.endDate);
        const totalDays = end.diff(start, "days");
        const groupEnd = start.clone().add(Math.round(totalDays * 0.8), "days");
        const koStart = groupEnd.clone().add(1, "day").isAfter(end) ? end.clone() : groupEnd.clone().add(1, "day");

        // Shorten the group phase end date
        d.matchSchedule.endDate = groupEnd.format("YYYY-MM-DD");
        koSchedule = {
          ...d.matchSchedule,
          startDate: koStart.format("YYYY-MM-DD"),
          endDate: end.format("YYYY-MM-DD"),
        };
      }
      setPhase2Settings({
        ...INITIAL_PHASE2_SETTINGS,
        availablePitches: d.availablePitches,
        matchRules: { ...d.matchRules },
        matchSchedule: koSchedule,
      });
      setShowAdvanced(false);
      setCustomTeams(false);
      setMatchDays(ALL_DAYS);
      setKnockoutMatchDays(ALL_DAYS);
    }
  }, [isOpen, templateType, selectedTournament, previousPhase, smartGroupConfig, smartBracketTeamsCount]);

  // ── Competition date window ─────────────────────────────
  const competitionStart = selectedTournament?.matchSchedule?.startDate || "";
  const competitionEnd = selectedTournament?.matchSchedule?.endDate || "";

  // ── Helpers ─────────────────────────────────────────────
  const updateField = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));
  const updateSchedule = (field, value) =>
    setFormData((prev) => ({
      ...prev,
      matchSchedule: { ...prev.matchSchedule, [field]: value },
    }));
  const updateRule = (field, value) =>
    setFormData((prev) => ({
      ...prev,
      matchRules: { ...prev.matchRules, [field]: value },
    }));

  const togglePitch = (pitch) => {
    setFormData((prev) => {
      const current = prev.availablePitches || [];
      const next = current.includes(pitch)
        ? current.filter((p) => p !== pitch)
        : [...current, pitch];
      return { ...prev, availablePitches: next };
    });
  };

  const allPitches = useMemo(() => getAllPitches(selectedTournament), [selectedTournament]);

  // Toggle a match day on/off
  const toggleMatchDay = (day) => {
    setMatchDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };
  const toggleKnockoutMatchDay = (day) => {
    setKnockoutMatchDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  // ── Submit ──────────────────────────────────────────────
  const handleCreate = () => {
    const submittedFormData = {
      ...formData,
      matchSchedule: {
        ...formData.matchSchedule,
        matchDays,
      },
    };

    const extraConfig = {
      templateType,
      groupConfig,
      bracketConfig,
      phase2Settings: {
        ...phase2Settings,
        matchSchedule: {
          ...((phase2Settings.matchSchedule) || {}),
          matchDays: knockoutMatchDays,
          matchTimings: formData.matchSchedule.matchTimings,
          startTime: phase2Settings.matchSchedule?.startTime || formData.matchSchedule.startTime,
          dayEndTime: phase2Settings.matchSchedule?.dayEndTime || formData.matchSchedule.dayEndTime,
        },
      },
    };
    onSubmit(submittedFormData, extraConfig);
  };

  // ── Render ──────────────────────────────────────────────
  const isGroupStageKnockout = templateType === "groupStageKnockout";
  const isKnockout = templateType === "knockoutPhase";
  const isGroup = templateType === "groupPhase";
  const showGroupFields = isGroup || isGroupStageKnockout;
  const showBracketFields = isKnockout || isGroupStageKnockout;

  // When the bracket size doesn't divide cleanly by the number of groups, the
  // backend fills the leftover slots with the best Nth-place teams across groups
  // ("best 3rd-place" logic for top-2 setups). Mirror that detection here so the
  // user is told upfront. The error variant disables submission because the
  // backend can't synthesise more advancers than groups.
  const advancementNotice = useMemo(() => {
    if (!isGroupStageKnockout) return null;
    const numGroups = Number(groupConfig.numGroups) || 0;
    const teamsPerGroup = Number(groupConfig.teamsPerGroup) || 0;
    const bracketTeams = Number(bracketConfig.teamsCount) || 0;
    if (numGroups < 1 || bracketTeams < 2) return null;
    const positionsPerGroup = Math.floor(bracketTeams / numGroups);
    const extraNeeded = bracketTeams - positionsPerGroup * numGroups;
    if (extraNeeded <= 0) return null;
    if (extraNeeded > numGroups || teamsPerGroup <= positionsPerGroup) {
      return {
        kind: "error",
        text: `This bracket is too large for ${numGroups} group${numGroups > 1 ? "s" : ""} of ${teamsPerGroup}. Reduce the bracket size or change the group setup.`,
      };
    }
    const ord = (n) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };
    return {
      kind: "info",
      topPerGroup: positionsPerGroup,
      remainingPlace: ord(positionsPerGroup + 1),
      remainingPlural: extraNeeded > 1,
    };
  }, [isGroupStageKnockout, groupConfig.numGroups, groupConfig.teamsPerGroup, bracketConfig.teamsCount]);

  return (
    <ClubbieModal
      isOpen={isOpen}
      toggle={onClose}
      title={`Create ${TEMPLATE_LABELS[templateType]}`}
      maxHeight="90vh"
      width="680px"
      footer={
        <div className="flex justify-end gap-3 w-full">
          <button
            type="button"
            className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bg-[#00E7FF] hover:bg-[#00cce6] text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleCreate}
            disabled={isLoading || advancementNotice?.kind === "error"}
          >
            {isLoading ? "Creating…" : `Create format`}
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Template chip */}
        {/* <div className="flex items-center gap-2">
          <span className="px-3 py-1 rounded-full bg-[#192634] text-[#00E7FF] text-xs font-medium">
            {TEMPLATE_LABELS[templateType]}
          </span>
        </div> */}

        {/* ── Primary fields ─────────────────────────────── */}

        {/* Group config */}
        {showGroupFields && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="block text-[14px] font-normal text-clubbie-white ">Number of groups</label>
                <NumberInput
                  value={groupConfig.numGroups}
                  onChange={(v) => setGroupConfig((p) => ({ ...p, numGroups: v }))}
                  min={1}
                  max={32}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="block text-[14px] font-normal text-clubbie-white ">Teams per group</label>
                <NumberInput
                  value={groupConfig.teamsPerGroup}
                  onChange={(v) => setGroupConfig((p) => ({ ...p, teamsPerGroup: v }))}
                  min={2}
                  max={64}
                />
              </div>
            </div>
            {/* Fixture Mode Selection */}
            <div className="flex flex-col gap-3">
              <label className="block text-[14px] font-normal text-clubbie-white ">Fixture scheduling mode</label>
              <div className="flex flex-col gap-[6px]">
                <div className="flex items-center gap-[12px]">
                  <label className="flex items-center gap-[12px] cursor-pointer">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${groupConfig.fixtureMode === "ROUND_ROBIN" ? "border-[#00E7FF]" : "border-[#333E48]"}`}>
                      {groupConfig.fixtureMode === "ROUND_ROBIN" && <div className="w-2.5 h-2.5 rounded-full bg-[#00E7FF]" />}
                    </div>
                    <input type="radio" className="hidden" checked={groupConfig.fixtureMode === "ROUND_ROBIN"} onChange={() => setGroupConfig((p) => ({ ...p, fixtureMode: "ROUND_ROBIN" }))} />
                    <span className="text-sm text-[#FEFCEB]">Times to face each other</span>
                  </label>
                  <InlineNumberInput
                    value={groupConfig.timesToFaceEachOther}
                    onChange={(v) => setGroupConfig((p) => ({ ...p, timesToFaceEachOther: v }))}
                    min={1}
                    max={4}
                    disabled={groupConfig.fixtureMode !== "ROUND_ROBIN"}
                  />
                </div>
                <div className="flex items-center gap-[12px]">
                  <label className="flex items-center gap-[12px] cursor-pointer">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${groupConfig.fixtureMode === "FIXED_MATCHES" ? "border-[#00E7FF]" : "border-[#333E48]"}`}>
                      {groupConfig.fixtureMode === "FIXED_MATCHES" && <div className="w-2.5 h-2.5 rounded-full bg-[#00E7FF]" />}
                    </div>
                    <input type="radio" className="hidden" checked={groupConfig.fixtureMode === "FIXED_MATCHES"} onChange={() => setGroupConfig((p) => ({ ...p, fixtureMode: "FIXED_MATCHES" }))} />
                    <span className="text-sm text-[#FEFCEB]">Number of matches per team</span>
                  </label>
                  <InlineNumberInput
                    value={groupConfig.matchesPerTeam}
                    onChange={(v) => setGroupConfig((p) => ({ ...p, matchesPerTeam: v }))}
                    min={1}
                    max={99}
                    disabled={groupConfig.fixtureMode !== "FIXED_MATCHES"}
                  />
                </div>
              </div>
            </div>
            {isGroupStageKnockout && (
              <>
                <div className="flex flex-col gap-2">
                  <label className="block text-[14px] font-normal text-clubbie-white ">Number of teams</label>
                  <div className="flex gap-2">
                    <div className="flex gap-2">
                      {TEAM_PRESETS.map((n) => (
                        <button
                          key={n}
                          type="button"
                          className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                            bracketConfig.teamsCount === n
                              ? "bg-app-surface  text-light-blue border border-light-blue"
                              : "bg-app-surface text-[#FEFCEB] hover:bg-[#192634]"
                          }`}
                          onClick={() => {
                              setBracketConfig((p) => ({ ...p, teamsCount: n }))
                              setCustomTeams(false)
                            }
                          }
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-[14px] font-normal text-clubbie-white ">Match format</label>
                  <ClubbieSelect
                    placeholder="Select format"
                    options={LEG_OPTIONS}
                    value={LEG_OPTIONS.find((o) => o.value === bracketConfig.numberOfLegs) || null}
                    onChange={(option) => setBracketConfig((p) => ({ ...p, numberOfLegs: option.value }))}
                    getOptionLabel={(o) => o.label}
                    getOptionValue={(o) => o.value}
                  />
                </div>
                {advancementNotice?.kind === "error" && (
                  <div className="flex items-start gap-2 rounded-lg px-3 py-2 text-sm border-red-500/40 bg-red-500/10 text-red-200">
                    <InfoIcon className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{advancementNotice.text}</span>
                  </div>
                )}
                {advancementNotice?.kind === "info" && (
                  <div className="flex flex-col gap-3">
                    <label className="block text-[14px] font-normal text-clubbie-white ">Knockout progression</label>
                    <div className="flex flex-col gap-[6px]">
                      <div className="flex items-center gap-[12px]">
                        <span className="text-sm text-app-text">Auto-progression:</span>
                        <span className="text-sm text-app-text">Top</span>
                        <span className="w-16 h-9 flex items-center justify-center bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] font-proxima  cursor-not-allowed">
                          {advancementNotice.topPerGroup}
                        </span>
                        <span className="text-sm text-app-text">from each group</span>
                      </div>
                      <div className="flex items-center gap-[12px]">
                        <span className="text-sm text-[#FEFCEB]">Remaining places:</span>
                        <span className="text-sm text-app-text">Best</span>
                        <span className="w-16 h-9 flex items-center justify-center bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] font-proxima cursor-not-allowed">
                          {advancementNotice.remainingPlace}
                        </span>
                        <span className="text-sm text-app-text">placed team{advancementNotice.remainingPlural ? "s" : ""}</span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Knockout-only config */}
        {isKnockout && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className="block text-[14px] font-normal text-clubbie-white ">Number of teams</label>     
              <div className="flex gap-2">
                <div className="flex gap-2">
                {TEAM_PRESETS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={`w-10 h-10 rounded-full text-sm font-medium transition-colors ${
                      bracketConfig.teamsCount === n
                        ? "bg-app-surface  text-light-blue border border-light-blue"
                        : "bg-app-surface text-[#FEFCEB] hover:bg-[#192634]"
                    }`}
                    onClick={() => {
                        setBracketConfig((p) => ({ ...p, teamsCount: n }))
                        setCustomTeams(false)
                      }
                    }
                  >
                    {n}
                  </button>
                ))}
                {/* <button
                  type="button"
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors
                        ${customTeams ? "text-light-blue border border-light-blue"
                        : "bg-app-surface text-[#FEFCEB] hover:bg-[#192634]"}
                    `}
                  onClick={() => { setCustomTeams(true)
                                   setBracketConfig((p) => ({ ...p, teamsCount: 64 }))

                   }}
                >
                  Custom
                </button> */}
                </div>
                {/* {customTeams && <PowerOfTwoInput
                  value={bracketConfig.teamsCount}
                  onChange={(v) => setBracketConfig((p) => ({ ...p, teamsCount: v }))}
                  min={2}
                  max={128}
                />} */}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="block text-[14px] font-normal text-clubbie-white ">Match format</label>
              <ClubbieSelect
                placeholder="Select format"
                options={LEG_OPTIONS}
                value={LEG_OPTIONS.find((o) => o.value === bracketConfig.numberOfLegs) || null}
                onChange={(option) => setBracketConfig((p) => ({ ...p, numberOfLegs: option.value }))}
                getOptionLabel={(o) => o.label}
                getOptionValue={(o) => o.value}
              />
            </div>
          </div>
        )}

        {/* ── Match days + Date strips ────────────────────── */}

        {/* Single phase: date pickers — hidden, defaults always sent */}
        <div className="hidden">
        {!isGroupStageKnockout && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-[14px] font-normal text-clubbie-white ">Start date</label>
                <DatePicker
                  value={formData.matchSchedule.startDate}
                  onChange={(date) => updateSchedule("startDate", date)}
                  minDate={competitionStart}
                  maxDate={competitionEnd}
                  disablePastDates={false}
                />
              </div>
              <div className="space-y-2">
                <label className="block text-[14px] font-normal text-clubbie-white ">End date</label>
                <DatePicker
                  value={formData.matchSchedule.endDate}
                  onChange={(date) => updateSchedule("endDate", date)}
                  minDate={formData.matchSchedule.startDate || competitionStart}
                  maxDate={competitionEnd}
                  disablePastDates={false}
                />
              </div>
            </div>
          </>
        )}

        {/* Group + Knockout: separate match day selectors + date pickers */}
        {isGroupStageKnockout && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-[14px] font-normal text-clubbie-white ">Group start date</label>
                <DatePicker
                  value={formData.matchSchedule.startDate}
                  onChange={(date) => updateSchedule("startDate", date)}
                  minDate={competitionStart}
                  maxDate={competitionEnd}
                  disablePastDates={false}
                />
              </div>
              <div className="space-y-2">
                <label className="block text-[14px] font-normal text-clubbie-white ">Group end date</label>
                <DatePicker
                  value={formData.matchSchedule.endDate}
                  onChange={(date) => updateSchedule("endDate", date)}
                  minDate={formData.matchSchedule.startDate || competitionStart}
                  maxDate={competitionEnd}
                  disablePastDates={false}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-[14px] font-normal text-clubbie-white ">Knockout start date</label>
                <DatePicker
                  value={phase2Settings.matchSchedule?.startDate || ""}
                  onChange={(date) => setPhase2Settings((p) => ({
                    ...p,
                    matchSchedule: { ...(p.matchSchedule || {}), startDate: date },
                  }))}
                  minDate={competitionStart}
                  maxDate={competitionEnd}
                  disablePastDates={false}
                />
              </div>
              <div className="space-y-2">
                <label className="block text-[14px] font-normal text-clubbie-white ">Knockout end date</label>
                <DatePicker
                  value={phase2Settings.matchSchedule?.endDate || ""}
                  onChange={(date) => setPhase2Settings((p) => ({
                    ...p,
                    matchSchedule: { ...(p.matchSchedule || {}), endDate: date },
                  }))}
                  minDate={phase2Settings.matchSchedule?.startDate || competitionStart}
                  maxDate={competitionEnd}
                  disablePastDates={false}
                />
              </div>
            </div>
          </>
        )}
        </div>

        {/* ── More settings (collapsed) — hidden, defaults always sent ── */}
        <div className="hidden">
        <div>
          <button
            type="button"
            className="flex items-center gap-2 text-sm text-app-text-muted hover:text-[#FEFCEB] transition-colors"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            <span className="text-[16px]">{showAdvanced ? "▾" : "▸"}</span>
            More settings
          </button>

          {showAdvanced && (
            <div className="mt-4 space-y-5 border-t border-[#192634] pt-4">
              {/* Phase name */}
              <div className="space-y-2">
                <label className="block text-[14px] font-normal text-clubbie-white ">
                  Phase name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => updateField("name", e.target.value)}
                  className="w-full h-12 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] placeholder-app-text-secondary font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors"
                />
              </div>

              {/* Points system — only relevant for group stages */}
              {showGroupFields && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-[14px] font-normal text-clubbie-white ">
                      Points for Win
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={formData.matchRules.pointsForWin}
                      onChange={(e) => updateRule("pointsForWin", e.target.value)}
                      className="w-full h-12 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] placeholder-app-text-secondary font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-[14px] font-normal text-clubbie-white ">
                      Points for Draw
                    </label>
                    <input
                      type="number"
                      min={0}
                      value={formData.matchRules.pointsForDraw}
                      onChange={(e) => updateRule("pointsForDraw", e.target.value)}
                      className="w-full h-12 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] placeholder-app-text-secondary font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors"
                    />
                  </div>
                </div>
              )}

              {/* Match timing */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-[14px] font-normal text-clubbie-white ">
                    Duration per segment (min)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={formData.matchRules.gameDuration}
                    onChange={(e) => updateRule("gameDuration", e.target.value)}
                    className="w-full h-12 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] placeholder-app-text-secondary font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-[14px] font-normal text-clubbie-white ">
                    Segments per match
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={formData.matchRules.breaks}
                    onChange={(e) => updateRule("breaks", e.target.value)}
                    className="w-full h-12 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] placeholder-app-text-secondary font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-[14px] font-normal text-clubbie-white ">
                    Break duration (min)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={formData.matchRules.breakDuration}
                    onChange={(e) => updateRule("breakDuration", e.target.value)}
                    className="w-full h-12 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] placeholder-app-text-secondary font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-[14px] font-normal text-clubbie-white ">
                    Time between matches (min)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={formData.matchRules.intervalBetweenMatches}
                    onChange={(e) => updateRule("intervalBetweenMatches", e.target.value)}
                    className="w-full h-12 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] placeholder-app-text-secondary font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors"
                  />
                </div>
              </div>

              {/* Day hours */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-[14px] font-normal text-clubbie-white ">
                    Day start time
                  </label>
                  <TimePicker
                    value={formData.matchSchedule.startTime || "09:00"}
                    onChange={(val) => updateSchedule("startTime", val)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-[14px] font-normal text-clubbie-white ">
                    Day end time
                  </label>
                  <TimePicker
                    value={formData.matchSchedule.dayEndTime || "21:00"}
                    onChange={(val) => updateSchedule("dayEndTime", val)}
                  />
                </div>
              </div>

              {/* Pitches */}
              {allPitches.length > 0 && (
                <div className="space-y-2">
                  <label className="block text-[14px] font-normal text-clubbie-white ">
                    Available pitches
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {allPitches.map((pitch) => {
                      const checked = (formData.availablePitches || []).includes(pitch);
                      return (
                        <label
                          key={pitch}
                          className={`px-3 py-1.5 rounded-full text-sm cursor-pointer transition-colors ${
                            checked
                              ? "bg-[#00E7FF] text-[#021529]"
                              : "bg-app-card text-[#FEFCEB] hover:bg-[#192634]"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => togglePitch(pitch)}
                            className="hidden"
                          />
                          {pitch}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Match days */}
              <div className="space-y-2">
                <label className="block text-[14px] font-normal text-clubbie-white ">
                  {isGroupStageKnockout ? "Group stage match days" : "Match days"}
                </label>
                <div className="flex flex-wrap gap-2">
                  {ALL_DAYS.map((day) => {
                    const checked = matchDays.includes(day);
                    return (
                      <label
                        key={day}
                        className={`px-3 py-1.5 rounded-full text-sm cursor-pointer transition-colors ${
                          checked
                            ? "bg-[#00E7FF] text-[#021529]"
                            : "bg-app-card text-[#FEFCEB] hover:bg-[#192634]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleMatchDay(day)}
                          className="hidden"
                        />
                        {day}
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Group-specific advanced */}
              {showGroupFields && (
                <div className="space-y-2">
                  <label className="block text-[14px] font-normal text-clubbie-white ">
                    Group name prefix
                  </label>
                  <input
                    type="text"
                    value={groupConfig.groupNamePrefix}
                    onChange={(e) =>
                      setGroupConfig((p) => ({ ...p, groupNamePrefix: e.target.value }))
                    }
                    className="w-full h-12 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] placeholder-app-text-secondary font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors"
                  />
                </div>
              )}

              {/* Knockout-specific advanced (for standalone knockout) */}
              {isKnockout && (
                <div className="space-y-2">
                  <label className="block text-[14px] font-normal text-clubbie-white ">
                    Bracket name
                  </label>
                  <input
                    type="text"
                    value={bracketConfig.bracketName}
                    onChange={(e) =>
                      setBracketConfig((p) => ({ ...p, bracketName: e.target.value }))
                    }
                    className="w-full h-12 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] placeholder-app-text-secondary font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors"
                  />
                </div>
              )}

              {/* Group + Knockout: knockout phase settings */}
              {isGroupStageKnockout && (
                <div className="space-y-4 border-t border-[#192634] pt-4">
                  <h6 className="text-[14px] font-semibold text-[#FEFCEB]">Knockout phase settings</h6>

                  <div className="space-y-2">
                    <label className="block text-[14px] font-normal text-clubbie-white ">
                      Knockout phase name
                    </label>
                    <input
                      type="text"
                      value={phase2Settings.name || "Knockout stage"}
                      onChange={(e) =>
                        setPhase2Settings((p) => ({ ...p, name: e.target.value }))
                      }
                      className="w-full h-12 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] placeholder-app-text-secondary font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-[14px] font-normal text-clubbie-white ">
                      Bracket name
                    </label>
                    <input
                      type="text"
                      value={bracketConfig.bracketName}
                      onChange={(e) =>
                        setBracketConfig((p) => ({ ...p, bracketName: e.target.value }))
                      }
                      className="w-full h-12 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] placeholder-app-text-secondary font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors"
                    />
                  </div>

                      {/* Knockout pitches */}
                      {allPitches.length > 0 && (
                        <div className="space-y-2">
                          <label className="block text-[14px] font-normal text-clubbie-white ">
                            Knockout pitches
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {allPitches.map((pitch) => {
                              const checked = (phase2Settings.availablePitches || []).includes(pitch);
                              return (
                                <label
                                  key={pitch}
                                  className={`px-3 py-1.5 rounded-full text-sm cursor-pointer transition-colors ${
                                    checked
                                      ? "bg-[#00E7FF] text-[#021529]"
                                      : "bg-app-card text-[#FEFCEB] hover:bg-[#192634]"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => {
                                      const current = phase2Settings.availablePitches || [];
                                      const next = current.includes(pitch)
                                        ? current.filter((p) => p !== pitch)
                                        : [...current, pitch];
                                      setPhase2Settings((p) => ({ ...p, availablePitches: next }));
                                    }}
                                    className="hidden"
                                  />
                                  {pitch}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Knockout match timing */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="block text-[14px] font-normal text-clubbie-white ">
                            Duration per segment (min)
                          </label>
                          <input
                            type="number"
                            min={0}
                            value={phase2Settings.matchRules?.gameDuration || ""}
                            onChange={(e) =>
                              setPhase2Settings((p) => ({
                                ...p,
                                matchRules: { ...(p.matchRules || {}), gameDuration: e.target.value },
                              }))
                            }
                            className="w-full h-12 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] placeholder-app-text-secondary font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-[14px] font-normal text-clubbie-white ">
                            Segments per match
                          </label>
                          <input
                            type="number"
                            min={1}
                            value={phase2Settings.matchRules?.breaks || ""}
                            onChange={(e) =>
                              setPhase2Settings((p) => ({
                                ...p,
                                matchRules: { ...(p.matchRules || {}), breaks: e.target.value },
                              }))
                            }
                            className="w-full h-12 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] placeholder-app-text-secondary font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-[14px] font-normal text-clubbie-white ">
                            Break duration (min)
                          </label>
                          <input
                            type="number"
                            min={0}
                            value={phase2Settings.matchRules?.breakDuration || ""}
                            onChange={(e) =>
                              setPhase2Settings((p) => ({
                                ...p,
                                matchRules: { ...(p.matchRules || {}), breakDuration: e.target.value },
                              }))
                            }
                            className="w-full h-12 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] placeholder-app-text-secondary font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-[14px] font-normal text-clubbie-white ">
                            Time between matches (min)
                          </label>
                          <input
                            type="number"
                            min={0}
                            value={phase2Settings.matchRules?.intervalBetweenMatches || ""}
                            onChange={(e) =>
                              setPhase2Settings((p) => ({
                                ...p,
                                matchRules: { ...(p.matchRules || {}), intervalBetweenMatches: e.target.value },
                              }))
                            }
                            className="w-full h-12 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] placeholder-app-text-secondary font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors"
                          />
                        </div>
                      </div>

                      {/* Knockout day hours */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="block text-[14px] font-normal text-clubbie-white ">
                            Day start time
                          </label>
                          <TimePicker
                            value={phase2Settings.matchSchedule?.startTime || "09:00"}
                            onChange={(val) =>
                              setPhase2Settings((p) => ({
                                ...p,
                                matchSchedule: { ...(p.matchSchedule || {}), startTime: val },
                              }))
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="block text-[14px] font-normal text-clubbie-white ">
                            Day end time
                          </label>
                          <TimePicker
                            value={phase2Settings.matchSchedule?.dayEndTime || "21:00"}
                            onChange={(val) =>
                              setPhase2Settings((p) => ({
                                ...p,
                                matchSchedule: { ...(p.matchSchedule || {}), dayEndTime: val },
                              }))
                            }
                          />
                        </div>
                      </div>

                      {/* Knockout match days */}
                      <div className="space-y-2">
                        <label className="block text-[14px] font-normal text-clubbie-white ">
                          Knockout phase match days
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {ALL_DAYS.map((day) => {
                            const checked = knockoutMatchDays.includes(day);
                            return (
                              <label
                                key={day}
                                className={`px-3 py-1.5 rounded-full text-sm cursor-pointer transition-colors ${
                                  checked
                                    ? "bg-[#00E7FF] text-[#021529]"
                                    : "bg-app-card text-[#FEFCEB] hover:bg-[#192634]"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleKnockoutMatchDay(day)}
                                  className="hidden"
                                />
                                {day}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      </div>
    </ClubbieModal>
  );
};

/**
 * Small inline number input with increment/decrement styling.
 */
const NumberInput = ({ value, onChange, min = 0, max = 999 }) => (
  <input
    type="number"
    value={value}
    onChange={(e) => {
      const v = parseInt(e.target.value);
      if (!isNaN(v) && v >= min && v <= max) onChange(v);
    }}
    min={min}
    max={max}
    className="w-full h-10 px-4 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors"
  />
);

const InlineNumberInput = ({ value, onChange, min = 0, max = 999, disabled = false }) => (
  <input
    type="number"
    value={disabled ? "" : value}
    disabled={disabled}
    onChange={(e) => {
      const v = parseInt(e.target.value);
      if (!isNaN(v) && v >= min && v <= max) onChange(v);
    }}
    min={min}
    max={max}
    className={`w-16 h-9 px-3 bg-app-surface border border-app-surface rounded-full text-[14px] text-[#FEFCEB] font-proxima focus:outline-none focus:border-[#00E7FF] transition-colors text-center ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
  />
);

const PowerOfTwoInput = ({ value, onChange, min = 2, max = 128 }) => {
  const canDec = value > min;
  const canInc = value < max;
  return (
    <div className="h-10 bg-app-surface border border-app-surface rounded-full flex items-center px-1 gap-1">
      <button
        type="button"
        disabled={!canDec}
        onClick={() => onChange(Math.max(min, value / 2))}
        className="w-8 h-8 flex items-center justify-center rounded-full text-[#FEFCEB] hover:bg-[#192634] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
      >
        −
      </button>
      <div className="min-w-[2.5rem] px-2 text-center text-[14px] text-[#FEFCEB] font-proxima">
        {value}
      </div>
      <button
        type="button"
        disabled={!canInc}
        onClick={() => onChange(Math.min(max, value * 2))}
        className="w-8 h-8 flex items-center justify-center rounded-full text-[#FEFCEB] hover:bg-[#192634] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
      >
        +
      </button>
    </div>
  );
};

export default CreateFormatModal;
